import { type AnchorHTMLAttributes, type ReactNode, type Ref } from 'react';
import { Link } from 'react-router';

import { getHomepageUrl } from '@cartwhl/common/utils/getHomepageUrl';

interface NavigationLinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children' | 'href'> {
  children: ReactNode;
  className?: string;
  external?: boolean;
  href: string;
  onNavigate?: () => void;
  ref?: Ref<HTMLAnchorElement>;
}

export default function NavigationLink({
  children,
  className,
  external = false,
  href,
  onNavigate,
  onClick,
  ref,
  ...props
}: NavigationLinkProps) {
  const classNameValue = className ?? '';

  function handleClick(event: React.MouseEvent<HTMLAnchorElement>) {
    onClick?.(event);
    if (!event.defaultPrevented) onNavigate?.();
  }

  if (external) {
    return (
      <a
        {...props}
        href={href}
        className={classNameValue}
        ref={ref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
      >
        {children}
      </a>
    );
  }

  const isOnHomepage =
    typeof window === 'undefined' ||
    getHomepageUrl('/') === `${window.location.origin}/`;
  if (isOnHomepage) {
    return (
      <Link
        {...props}
        to={href}
        className={classNameValue}
        ref={ref}
        onClick={handleClick}
      >
        {children}
      </Link>
    );
  }

  return (
    <a
      {...props}
      href={getHomepageUrl(href)}
      className={classNameValue}
      ref={ref}
      onClick={handleClick}
    >
      {children}
    </a>
  );
}
